import { Widget } from '@/components/widget';
import { useUserConfig } from '@/hooks/user/use-user-config';
import { useUserOrganisationUsers } from '@/hooks/user/use-user-organisation-users';
import { useUserDisable } from '@/hooks/user/use-user-disable';
import { useUserEnable } from '@/hooks/user/use-user-enable';
import { useUserSendResetPasswordEmail } from '@/hooks/user/use-user-send-reset-password-email';
import { useEffect, useState } from 'react';
import { useOrganisationUserPermissions } from '@/hooks/user/use-organisation-user-permissions';
import { useOrganisationUserPermissionsUpdate } from '@/hooks/user/use-organisation-user-permissions-update';
import { useListener } from '@/hooks/use-listener';
import { useUserAvailableRealmRoles } from '@/hooks/user/use-realm-roles';
import { Toggle } from '@/components/toggle';
import { useAuthorisedFunction } from '@/utilities/authorised-function';
import { Button } from '@/components/button';

export function PermissionsWidget({
  id = 'permissions',
  hideable = false,
} : {
  id?: string;
  hideable?: boolean;
}) {
  const { data: userConfigData } = useUserConfig();
  const { mutateAsync: getOrganisationUserPermissions } =
    useOrganisationUserPermissions();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [originalPermissions, setOriginalPermissions] = useState<string[]>([]);
  const { mutateAsync: updateOrganisationUserPermissions } =
    useOrganisationUserPermissionsUpdate();
  const [user, setUser] = useState<any>(null);
  const { data: realmRoles } = useUserAvailableRealmRoles();
  const [roles, setRoles] = useState<string[]>([]);
  const [originalRoles, setOriginalRoles] = useState<string[]>([]);
  const { isAuthorised } = useAuthorisedFunction();
  const [saving, setSaving] = useState<boolean>(false);


  useListener({
    id: id,
    listenerFn: (data) => {
      setUser(data[0].user);
      handleFetchOrganisationUserPermissions(data[0].user.id);
    },
    type: 'UserPermissions',
  });

  const handleFetchOrganisationUserPermissions = async (userUuid: string) => {
    const response = await getOrganisationUserPermissions({ uuid: userUuid });
    setOriginalPermissions(response.data.permissions);
    setPermissions(response.data.permissions);
    setOriginalRoles(response.data.roles);
    setRoles(response.data.roles);
  };

  const handlePermissionToggle = (permission: string) => {
    setPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(perm => perm !== permission)
        : [...prev, permission]
    );
  };

  const handlePermissionsSave = async (userUuid: string) => {
    setSaving(true);
    const addedPermissions = permissions.filter(
      (perm) => !originalPermissions.includes(perm)
    );
  
    const removedPermissions = originalPermissions.filter(
      (perm) => !permissions.includes(perm)
    );

    const addedRoles = roles.filter(
      (role) => !originalRoles.includes(role)
    );
  
    const removedRoles = originalRoles.filter(
      (role) => !roles.includes(role)
    );

    await updateOrganisationUserPermissions({
      uuid: userUuid,
      addedPermissions,
      removedPermissions,
      addedRoles,
      removedRoles,
    });

    handleFetchOrganisationUserPermissions(userUuid);
    setSaving(false);
  };

  const handleRoleToggle = (role: string) => {
    setRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  return (
    <Widget 
      id={id}
      title={'Permissions'}
      hideable={hideable}
      minHeight={500}
    >
      {!user && (
        <p className="mt-1 text-sm text-gray-500 mb-6">Select a user to manage permissions.</p>
      )}

      { isAuthorised(['developer']) && (
        <>
          <h2 className="text-base font-semibold leading-6 text-gray-900">
            Roles
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage roles for {user?.username ?? 'this user'}.
          </p>

          <div className="mt-6 overflow-x-auto">
            <div className="rounded-md border border-gray-200 overflow-hidden">
              <table className="min-w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                      Role
                    </th>
                    <th className="w-32 px-4 py-2 text-center text-sm font-semibold text-gray-700">
                      Assigned
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {realmRoles?.realmRoles.map((role: any) => {
                      const roleName = role.name;

                      return (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            {roleName}
                            <span className="block text-gray-500 text-xs font-normal">
                              {role.description}
                            </span>
                          </td>

                          <td className="w-32 px-4 py-3 text-center">
                            <Toggle
                              enabled={roles.includes(roleName)}
                              onChange={() => handleRoleToggle(roleName)}
                              disabled={!user}
                              size='small'
                            />
                            {/* <label className="inline-flex items-center justify-center">
                              <input
                                type="checkbox"
                                className="absolute inset-0 opacity-0 peer"
                                checked={roles.includes(roleName)}
                                onChange={() => handleRoleToggle(roleName)}
                                disabled={!user}
                              />
                              <div className="shrink-0 relative w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                            </label> */}
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <h2 className="mt-10 text-base font-semibold leading-6 text-gray-900">
        Container Permissions
      </h2>
      <p className="mt-1 text-sm text-gray-500">
        Manage container permissions for {user?.username ?? 'this user'}.
      </p>

      <div className="mt-6 overflow-x-auto">
        <div className="rounded-md border border-gray-200 overflow-hidden">
          <table className="min-w-full table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                  Container
                </th>
                <th className="w-32 px-4 py-2 text-center text-sm font-semibold text-gray-700">
                  Read
                </th>
                <th className="w-32 px-4 py-2 text-center text-sm font-semibold text-gray-700">
                  Write
                </th>
                <th className="w-32 px-4 py-2 text-center text-sm font-semibold text-gray-700">
                  Delete
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {userConfigData?.organisationConfig?.containers?.map(
                (container: any) => {
                  const readPerm = `container.${container.value}.read`;
                  const writePerm = `container.${container.value}.write`;
                  const deletePerm = `container.${container.value}.delete`;

                  return (
                    <tr key={container.value} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {container.label}
                      </td>

                      <td className="w-32 px-4 py-3 text-center">
                        <Toggle
                          enabled={permissions.includes(readPerm) || roles.includes('containers.organisation.read') || roles.includes('containers.organisation.crud')}
                          onChange={() => handlePermissionToggle(readPerm)}
                          disabled={!user}
                          size='small'
                        />
                        {/* <label className="inline-flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="absolute inset-0 opacity-0 peer"
                            checked={permissions.includes(readPerm) || roles.includes('containers.organisation.read')}
                            onChange={() => handlePermissionToggle(readPerm)}
                            disabled={!user}
                          />
                          <div className="shrink-0 relative w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                        </label> */}
                      </td>

                      <td className="w-32 px-4 py-3 text-center">
                        <Toggle
                          enabled={permissions.includes(writePerm) || roles.includes('containers.organisation.write') || roles.includes('containers.organisation.crud')}
                          onChange={() => handlePermissionToggle(writePerm)}
                          disabled={!user}
                          size='small'
                        />
                        {/* <label className="inline-flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="absolute inset-0 opacity-0 peer"
                            checked={permissions.includes(writePerm) || roles.includes('containers.organisation.write')}
                            onChange={() => handlePermissionToggle(writePerm)}
                            disabled={!user}
                          />
                          <div className="shrink-0 relative w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                        </label> */}
                      </td>

                      <td className="w-32 px-4 py-3 text-center">
                        <Toggle
                          enabled={permissions.includes(deletePerm) || roles.includes('containers.organisation.delete') || roles.includes('containers.organisation.crud')}
                          onChange={() => handlePermissionToggle(deletePerm)}
                          disabled={!user}
                          size='small'
                        />
                        {/* <label className="inline-flex items-center justify-center">
                          <input
                            type="checkbox"
                            className="absolute inset-0 opacity-0 peer"
                            checked={permissions.includes(deletePerm) || roles.includes('containers.organisation.delete')}
                            onChange={() => handlePermissionToggle(deletePerm)}
                            disabled={!user}
                          />
                          <div className="shrink-0 relative w-9 h-5 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                        </label> */}
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button 
          type="submit"
          loading={saving}
          disabled={!user}
          variant="tertiary"
          onClick={() =>
            handlePermissionsSave(
              user ? user.id : ''
            )
          }
        >
          Save
        </Button>
      </div>
    </Widget>
  );
}